import type {
  Aircraft,
  AircraftStatusFilters,
  FlightStatusFilters,
  ServiceabilityFilters,
} from '../../models/aircraft';

export type FleetRegistrationSort = 'asc' | 'desc';

export type ApplyAircraftFiltersParams = {
  aircrafts: Aircraft[];
  serviceabilityFilters: ServiceabilityFilters;
  aircraftStatusFilters: AircraftStatusFilters;
  flightStatusFilters: FlightStatusFilters;
  operatorFilter: string[] | null;
  typeFilter: string[] | null;
  searchText: string;
  sortBy: FleetRegistrationSort;
};

function aircraftMatchesSelectedStatus(aircraft: Aircraft, selected: string[]): boolean {
  if (selected.includes('locked')) {
    return selected.includes(aircraft.billing_status.toLowerCase()) || aircraft.locked;
  }
  return selected.includes(aircraft.billing_status.toLowerCase());
}

export function applyAircraftFilters({
  aircrafts,
  serviceabilityFilters,
  aircraftStatusFilters,
  flightStatusFilters,
  operatorFilter,
  typeFilter,
  searchText,
  sortBy,
}: ApplyAircraftFiltersParams): Aircraft[] {
  const selectedServiceability = Object.keys(serviceabilityFilters).filter(
    (key) => serviceabilityFilters[key as keyof ServiceabilityFilters],
  );
  const selectedAircraftStatus = Object.keys(aircraftStatusFilters).filter(
    (key) => aircraftStatusFilters[key as keyof AircraftStatusFilters],
  );
  const selectedFlightStatus = Object.keys(flightStatusFilters).filter(
    (key) => flightStatusFilters[key as keyof FlightStatusFilters],
  );

  const operatorIds = operatorFilter ?? [];
  const typeIds = typeFilter ?? [];
  const operatorFilterActive = operatorIds.length > 0;
  const typeFilterActive = typeIds.length > 0;
  const searchTextActive = searchText.length > 0;
  const searchQuery = searchText.toLowerCase();

  const result = aircrafts.filter((aircraft) => {
    if (selectedServiceability.length > 0 && !selectedServiceability.includes(aircraft.maintenance_status)) {
      return false;
    }
    if (selectedAircraftStatus.length > 0 && !aircraftMatchesSelectedStatus(aircraft, selectedAircraftStatus)) {
      return false;
    }
    if (selectedFlightStatus.length > 0 && !selectedFlightStatus.includes(aircraft.flight_status)) {
      return false;
    }
    if (operatorFilterActive && !operatorIds.includes(aircraft.operator_id)) {
      return false;
    }
    if (typeFilterActive && !typeIds.includes(aircraft.aircraft_type.id)) {
      return false;
    }
    if (searchTextActive && !aircraft.registration.toLowerCase().includes(searchQuery)) {
      return false;
    }
    return true;
  });

  if (sortBy === 'asc') {
    result.sort((a, b) => (a.registration > b.registration ? 1 : -1));
  } else if (sortBy === 'desc') {
    result.sort((a, b) => (a.registration < b.registration ? 1 : -1));
  }

  return result;
}
